import type { Language } from '../i18n/translations'

export interface PageSeo {
  title: string
  description: string
  keywords: string
  ogType?: string
}

type SeoMap = Record<string, Record<Language, PageSeo>>

const SITE_NAME = 'Astriqo'

export const seoConfig: SeoMap = {
  home: {
    ru: {
      title: `${SITE_NAME} — Профессиональная астрология: натальные карты, прогнозы, консультации`,
      description:
        'Платформа для глубокого самопознания через астрологию. Авто-отчёты по натальной карте, соляру, совместимости. Консультации с проверенными астрологами.',
      keywords:
        'астрология, натальная карта, гороскоп, соляр, синастрия, совместимость, прогноз, транзиты, консультация астролога',
    },
    en: {
      title: `${SITE_NAME} — Professional Astrology: Birth Charts, Forecasts, Consultations`,
      description:
        'A platform for deep self-discovery through astrology. Auto-reports for birth charts, solar returns, compatibility. Consultations with verified astrologers.',
      keywords:
        'astrology, birth chart, horoscope, solar return, synastry, compatibility, forecast, transits, astrologer consultation',
    },
    zh: {
      title: `${SITE_NAME} — 专业占星术：本命盘、预测、咨询`,
      description:
        '通过占星术深度自我认知的平台。本命盘、太阳回归、合盘自动报告。与认证占星师咨询。',
      keywords:
        '占星术, 本命盘, 星座运势, 太阳回归, 合盘, 兼容性, 预测, 行运, 占星师咨询',
    },
    es: {
      title: `${SITE_NAME} — Astrología Profesional: Cartas Natales, Pronósticos, Consultas`,
      description:
        'Plataforma de autoconocimiento profundo a través de la astrología. Informes automáticos de carta natal, revolución solar, compatibilidad. Consultas con astrólogos verificados.',
      keywords:
        'astrología, carta natal, horóscopo, revolución solar, sinastría, compatibilidad, pronóstico, tránsitos, consulta astrológica',
    },
  },

  'service-natal': {
    ru: {
      title: `Натальная карта — Полный анализ личности | ${SITE_NAME}`,
      description:
        'Закажите подробный отчёт по натальной карте: характер, таланты, карьера, отношения, здоровье. PDF от 3 900 ₽. Точность расчётов — швейцарские эфемериды.',
      keywords:
        'натальная карта, расчёт натальной карты, анализ натальной карты, гороскоп рождения, астрологический отчёт',
    },
    en: {
      title: `Birth Chart — Full Personality Analysis | ${SITE_NAME}`,
      description:
        'Order a detailed birth chart report: character, talents, career, relationships, health. PDF report with Swiss ephemeris precision.',
      keywords:
        'birth chart, natal chart, natal chart analysis, birth horoscope, astrological report',
    },
    zh: {
      title: `本命盘 — 完整性格分析 | ${SITE_NAME}`,
      description:
        '订购详细的本命盘报告：性格、才能、事业、关系、健康。瑞士星历表精确计算的PDF报告。',
      keywords: '本命盘, 本命盘分析, 出生星盘, 占星报告',
    },
    es: {
      title: `Carta Natal — Análisis Completo de Personalidad | ${SITE_NAME}`,
      description:
        'Solicita un informe detallado de carta natal: carácter, talentos, carrera, relaciones, salud. Informe PDF con precisión de efemérides suizas.',
      keywords:
        'carta natal, análisis carta natal, horóscopo natal, informe astrológico',
    },
  },

  'service-solar': {
    ru: {
      title: `Соляр (Солнечное возвращение) — Прогноз на год | ${SITE_NAME}`,
      description:
        'Соляр — прогноз на ваш персональный год. Ключевые темы, возможности и вызовы. Подробный PDF-отчёт.',
      keywords:
        'соляр, солнечное возвращение, прогноз на год, астрологический прогноз, годовой гороскоп',
    },
    en: {
      title: `Solar Return — Year Ahead Forecast | ${SITE_NAME}`,
      description:
        'Solar return forecast for your personal year. Key themes, opportunities and challenges. Detailed PDF report.',
      keywords:
        'solar return, year forecast, astrological forecast, annual horoscope',
    },
    zh: {
      title: `太阳回归 — 年度预测 | ${SITE_NAME}`,
      description: '太阳回归年度预测。关键主题、机遇和挑战。详细PDF报告。',
      keywords: '太阳回归, 年度预测, 占星预测, 年度运势',
    },
    es: {
      title: `Revolución Solar — Pronóstico Anual | ${SITE_NAME}`,
      description:
        'Pronóstico de revolución solar para tu año personal. Temas clave, oportunidades y desafíos. Informe PDF detallado.',
      keywords:
        'revolución solar, pronóstico anual, pronóstico astrológico, horóscopo anual',
    },
  },

  'service-compatibility': {
    ru: {
      title: `Совместимость (Синастрия) — Анализ отношений | ${SITE_NAME}`,
      description:
        'Синастрия для пар: совместимость характеров, эмоциональная связь, сексуальная химия, потенциал отношений. PDF-отчёт.',
      keywords:
        'синастрия, совместимость, совместимость по натальной карте, астрология отношений, совместимость пар',
    },
    en: {
      title: `Compatibility (Synastry) — Relationship Analysis | ${SITE_NAME}`,
      description:
        'Synastry for couples: character compatibility, emotional connection, chemistry, relationship potential. PDF report.',
      keywords:
        'synastry, compatibility, natal chart compatibility, relationship astrology, couple compatibility',
    },
    zh: {
      title: `合盘（合相分析）— 关系分析 | ${SITE_NAME}`,
      description:
        '情侣合盘：性格兼容性、情感连接、化学反应、关系潜力。PDF报告。',
      keywords: '合盘, 兼容性, 本命盘兼容性, 关系占星, 情侣兼容性',
    },
    es: {
      title: `Compatibilidad (Sinastría) — Análisis de Relaciones | ${SITE_NAME}`,
      description:
        'Sinastría para parejas: compatibilidad de caracteres, conexión emocional, química, potencial de relación. Informe PDF.',
      keywords:
        'sinastría, compatibilidad, compatibilidad carta natal, astrología relaciones, compatibilidad parejas',
    },
  },

  'service-combo': {
    ru: {
      title: `Комбо: Натальная карта + Соляр — Полный анализ | ${SITE_NAME}`,
      description:
        'Два отчёта в одном: глубокий анализ личности + прогноз на год. Выгоднее на 20% чем по отдельности.',
      keywords:
        'комбо отчёт, натальная карта и соляр, полный астрологический анализ, пакет отчётов',
    },
    en: {
      title: `Combo: Birth Chart + Solar Return — Full Analysis | ${SITE_NAME}`,
      description:
        'Two reports in one: deep personality analysis + year forecast. 20% cheaper than separate orders.',
      keywords:
        'combo report, birth chart and solar return, full astrological analysis, report package',
    },
    zh: {
      title: `组合：本命盘 + 太阳回归 — 完整分析 | ${SITE_NAME}`,
      description: '两份报告合一：深度性格分析 + 年度预测。比单独购买便宜20%。',
      keywords: '组合报告, 本命盘和太阳回归, 完整占星分析, 报告套餐',
    },
    es: {
      title: `Combo: Carta Natal + Revolución Solar — Análisis Completo | ${SITE_NAME}`,
      description:
        'Dos informes en uno: análisis profundo de personalidad + pronóstico anual. 20% más barato que por separado.',
      keywords:
        'informe combo, carta natal y revolución solar, análisis astrológico completo, paquete de informes',
    },
  },

  'service-forecast': {
    ru: {
      title: `Прогноз (Транзиты) — Ключевые периоды и даты | ${SITE_NAME}`,
      description:
        'Персональный прогноз по транзитам: важные даты, благоприятные и сложные периоды. Помесячный PDF-отчёт.',
      keywords:
        'транзиты, астрологический прогноз, прогноз по транзитам, ключевые даты, персональный прогноз',
    },
    en: {
      title: `Forecast (Transits) — Key Periods & Dates | ${SITE_NAME}`,
      description:
        'Personal transit forecast: important dates, favorable and challenging periods. Monthly PDF report.',
      keywords:
        'transits, astrological forecast, transit forecast, key dates, personal forecast',
    },
    zh: {
      title: `预测（行运）— 关键时期和日期 | ${SITE_NAME}`,
      description: '个人行运预测：重要日期、有利和挑战性时期。月度PDF报告。',
      keywords: '行运, 占星预测, 行运预测, 关键日期, 个人预测',
    },
    es: {
      title: `Pronóstico (Tránsitos) — Períodos y Fechas Clave | ${SITE_NAME}`,
      description:
        'Pronóstico personal de tránsitos: fechas importantes, períodos favorables y desafiantes. Informe PDF mensual.',
      keywords:
        'tránsitos, pronóstico astrológico, pronóstico de tránsitos, fechas clave, pronóstico personal',
    },
  },

  'service-consultation': {
    ru: {
      title: `Консультация с астрологом — Живой разговор | ${SITE_NAME}`,
      description:
        'Персональная консультация с проверенным астрологом. Видеозвонок или чат. Ответы на ваши вопросы о карьере, отношениях, здоровье.',
      keywords:
        'консультация астролога, астролог онлайн, персональная консультация, астрологическая консультация',
    },
    en: {
      title: `Astrologer Consultation — Live Session | ${SITE_NAME}`,
      description:
        'Personal consultation with a verified astrologer. Video call or chat. Answers to your questions about career, relationships, health.',
      keywords:
        'astrologer consultation, online astrologer, personal consultation, astrological consultation',
    },
    zh: {
      title: `占星师咨询 — 实时对话 | ${SITE_NAME}`,
      description:
        '与认证占星师的个人咨询。视频通话或聊天。解答关于事业、关系、健康的问题。',
      keywords: '占星师咨询, 在线占星师, 个人咨询, 占星咨询',
    },
    es: {
      title: `Consulta con Astrólogo — Sesión en Vivo | ${SITE_NAME}`,
      description:
        'Consulta personal con un astrólogo verificado. Videollamada o chat. Respuestas a tus preguntas sobre carrera, relaciones, salud.',
      keywords:
        'consulta astrológica, astrólogo online, consulta personal, consulta de astrología',
    },
  },

  pricing: {
    ru: {
      title: `Цены и пакеты — Астрологические отчёты | ${SITE_NAME}`,
      description:
        'Цены на астрологические отчёты и консультации. Натальная карта от 3 900 ₽, соляр от 2 900 ₽. Пакетные предложения со скидкой.',
      keywords:
        'цены астрология, стоимость натальной карты, пакеты отчётов, астрологические услуги цены',
    },
    en: {
      title: `Pricing & Packages — Astrological Reports | ${SITE_NAME}`,
      description:
        'Prices for astrological reports and consultations. Birth chart, solar return, compatibility reports. Package deals with discounts.',
      keywords:
        'astrology prices, birth chart cost, report packages, astrological services pricing',
    },
    zh: {
      title: `价格与套餐 — 占星报告 | ${SITE_NAME}`,
      description:
        '占星报告和咨询价格。本命盘、太阳回归、合盘报告。套餐优惠折扣。',
      keywords: '占星价格, 本命盘费用, 报告套餐, 占星服务价格',
    },
    es: {
      title: `Precios y Paquetes — Informes Astrológicos | ${SITE_NAME}`,
      description:
        'Precios de informes astrológicos y consultas. Carta natal, revolución solar, compatibilidad. Ofertas de paquetes con descuento.',
      keywords:
        'precios astrología, costo carta natal, paquetes de informes, precios servicios astrológicos',
    },
  },

  astrologers: {
    ru: {
      title: `Каталог астрологов — Проверенные специалисты | ${SITE_NAME}`,
      description:
        'Выберите астролога для консультации. Рейтинги, отзывы, специализации. Только проверенные профессионалы.',
      keywords:
        'каталог астрологов, астролог онлайн, найти астролога, рейтинг астрологов, консультация астролога',
    },
    en: {
      title: `Astrologer Catalog — Verified Professionals | ${SITE_NAME}`,
      description:
        'Choose an astrologer for consultation. Ratings, reviews, specializations. Only verified professionals.',
      keywords:
        'astrologer catalog, online astrologer, find astrologer, astrologer rating, astrologer consultation',
    },
    zh: {
      title: `占星师目录 — 认证专业人士 | ${SITE_NAME}`,
      description:
        '选择占星师进行咨询。评分、评价、专业领域。仅限认证专业人士。',
      keywords: '占星师目录, 在线占星师, 寻找占星师, 占星师评分, 占星师咨询',
    },
    es: {
      title: `Catálogo de Astrólogos — Profesionales Verificados | ${SITE_NAME}`,
      description:
        'Elige un astrólogo para consulta. Calificaciones, reseñas, especializaciones. Solo profesionales verificados.',
      keywords:
        'catálogo astrólogos, astrólogo online, encontrar astrólogo, calificación astrólogos, consulta astrológica',
    },
  },

  interpretations: {
    ru: {
      title: `Астрологическая энциклопедия — База знаний | ${SITE_NAME}`,
      description:
        'Всё об астрологии: планеты в знаках и домах, аспекты, асцендент. Образовательные статьи для начинающих и продвинутых.',
      keywords:
        'астрологическая энциклопедия, трактовки, планеты в знаках, аспекты, асцендент, астрология обучение',
    },
    en: {
      title: `Astrology Encyclopedia — Knowledge Base | ${SITE_NAME}`,
      description:
        'Everything about astrology: planets in signs and houses, aspects, ascendant. Educational articles for beginners and advanced.',
      keywords:
        'astrology encyclopedia, interpretations, planets in signs, aspects, ascendant, astrology education',
    },
    zh: {
      title: `占星百科全书 — 知识库 | ${SITE_NAME}`,
      description:
        '关于占星术的一切：行星在星座和宫位、相位、上升星座。适合初学者和进阶者的教育文章。',
      keywords: '占星百科, 解读, 行星在星座, 相位, 上升星座, 占星教育',
    },
    es: {
      title: `Enciclopedia Astrológica — Base de Conocimiento | ${SITE_NAME}`,
      description:
        'Todo sobre astrología: planetas en signos y casas, aspectos, ascendente. Artículos educativos para principiantes y avanzados.',
      keywords:
        'enciclopedia astrológica, interpretaciones, planetas en signos, aspectos, ascendente, educación astrología',
    },
  },

  calendar: {
    ru: {
      title: `Астро-календарь — Небесные события | ${SITE_NAME}`,
      description:
        'Календарь астрологических событий: ретрограды, затмения, полнолуния, новолуния. Планируйте важные дела с учётом космических ритмов.',
      keywords:
        'астро-календарь, астрологический календарь, ретроград Меркурия, затмения, полнолуние, новолуние',
    },
    en: {
      title: `Astro Calendar — Celestial Events | ${SITE_NAME}`,
      description:
        'Calendar of astrological events: retrogrades, eclipses, full moons, new moons. Plan important activities with cosmic rhythms.',
      keywords:
        'astro calendar, astrological calendar, Mercury retrograde, eclipses, full moon, new moon',
    },
    zh: {
      title: `星历 — 天象事件 | ${SITE_NAME}`,
      description:
        '占星事件日历：逆行、日食月食、满月、新月。根据宇宙节奏规划重要事务。',
      keywords: '星历, 占星日历, 水星逆行, 日食月食, 满月, 新月',
    },
    es: {
      title: `Calendario Astral — Eventos Celestes | ${SITE_NAME}`,
      description:
        'Calendario de eventos astrológicos: retrógrados, eclipses, lunas llenas, lunas nuevas. Planifica actividades importantes con ritmos cósmicos.',
      keywords:
        'calendario astral, calendario astrológico, Mercurio retrógrado, eclipses, luna llena, luna nueva',
    },
  },

  blog: {
    ru: {
      title: `Блог — Статьи и прогнозы | ${SITE_NAME}`,
      description:
        'Астрологический блог: еженедельные прогнозы, разборы затмений, советы по работе с энергиями. Полезные статьи для всех уровней.',
      keywords:
        'астрологический блог, астрология статьи, еженедельный прогноз, астрологические советы',
    },
    en: {
      title: `Blog — Articles & Forecasts | ${SITE_NAME}`,
      description:
        'Astrology blog: weekly forecasts, eclipse analysis, energy tips. Useful articles for all levels.',
      keywords:
        'astrology blog, astrology articles, weekly forecast, astrological tips',
    },
    zh: {
      title: `博客 — 文章与预测 | ${SITE_NAME}`,
      description:
        '占星博客：每周预测、日食分析、能量建议。适合所有水平的实用文章。',
      keywords: '占星博客, 占星文章, 每周预测, 占星建议',
    },
    es: {
      title: `Blog — Artículos y Pronósticos | ${SITE_NAME}`,
      description:
        'Blog de astrología: pronósticos semanales, análisis de eclipses, consejos energéticos. Artículos útiles para todos los niveles.',
      keywords:
        'blog astrología, artículos astrología, pronóstico semanal, consejos astrológicos',
    },
  },

  'free-chart': {
    ru: {
      title: `Бесплатный расчёт натальной карты | ${SITE_NAME}`,
      description:
        'Рассчитайте натальную карту бесплатно. Введите дату, время и место рождения — получите карту с основными положениями планет.',
      keywords:
        'бесплатная натальная карта, расчёт натальной карты онлайн, бесплатный гороскоп, карта рождения бесплатно',
    },
    en: {
      title: `Free Birth Chart Calculator | ${SITE_NAME}`,
      description:
        'Calculate your birth chart for free. Enter your date, time and place of birth — get your chart with key planetary positions.',
      keywords:
        'free birth chart, birth chart calculator online, free horoscope, free natal chart',
    },
    zh: {
      title: `免费本命盘计算 | ${SITE_NAME}`,
      description:
        '免费计算你的本命盘。输入出生日期、时间和地点 — 获取包含关键行星位置的星盘。',
      keywords: '免费本命盘, 在线本命盘计算, 免费星座运势, 免费星盘',
    },
    es: {
      title: `Cálculo Gratuito de Carta Natal | ${SITE_NAME}`,
      description:
        'Calcula tu carta natal gratis. Ingresa fecha, hora y lugar de nacimiento — obtén tu carta con posiciones planetarias clave.',
      keywords:
        'carta natal gratis, calculadora carta natal online, horóscopo gratis, carta natal gratuita',
    },
  },

  'daily-horoscope': {
    ru: {
      title: `Гороскоп на сегодня — Все знаки зодиака | ${SITE_NAME}`,
      description:
        'Ежедневный гороскоп для всех знаков зодиака. Узнайте, что приготовили звёзды на сегодня.',
      keywords:
        'гороскоп на сегодня, ежедневный гороскоп, гороскоп знаки зодиака, гороскоп на день',
    },
    en: {
      title: `Daily Horoscope — All Zodiac Signs | ${SITE_NAME}`,
      description:
        'Daily horoscope for all zodiac signs. Find out what the stars have in store for you today.',
      keywords:
        'daily horoscope, today horoscope, zodiac signs horoscope, horoscope today',
    },
    zh: {
      title: `今日运势 — 所有星座 | ${SITE_NAME}`,
      description: '所有星座的每日运势。了解今天星星为你准备了什么。',
      keywords: '今日运势, 每日运势, 星座运势, 今天运势',
    },
    es: {
      title: `Horóscopo de Hoy — Todos los Signos | ${SITE_NAME}`,
      description:
        'Horóscopo diario para todos los signos del zodíaco. Descubre qué te deparan las estrellas hoy.',
      keywords:
        'horóscopo hoy, horóscopo diario, horóscopo signos zodíaco, horóscopo del día',
    },
  },

  'moon-calendar': {
    ru: {
      title: `Лунный календарь — Фазы Луны | ${SITE_NAME}`,
      description:
        'Лунный календарь с фазами Луны, знаками зодиака и рекомендациями. Планируйте дела в гармонии с лунными циклами.',
      keywords:
        'лунный календарь, фазы луны, лунный день, луна в знаке, лунный цикл',
    },
    en: {
      title: `Moon Calendar — Lunar Phases | ${SITE_NAME}`,
      description:
        'Moon calendar with lunar phases, zodiac signs and recommendations. Plan activities in harmony with lunar cycles.',
      keywords:
        'moon calendar, lunar phases, moon day, moon in sign, lunar cycle',
    },
    zh: {
      title: `月历 — 月相 | ${SITE_NAME}`,
      description: '包含月相、星座和建议的月历。根据月亮周期和谐地规划活动。',
      keywords: '月历, 月相, 月亮日, 月亮在星座, 月亮周期',
    },
    es: {
      title: `Calendario Lunar — Fases de la Luna | ${SITE_NAME}`,
      description:
        'Calendario lunar con fases, signos zodiacales y recomendaciones. Planifica actividades en armonía con los ciclos lunares.',
      keywords:
        'calendario lunar, fases luna, día lunar, luna en signo, ciclo lunar',
    },
  },

  faq: {
    ru: {
      title: `Частые вопросы (FAQ) | ${SITE_NAME}`,
      description:
        'Ответы на частые вопросы об астрологических отчётах, консультациях, оплате и доставке.',
      keywords: 'FAQ, частые вопросы, астрология вопросы, помощь',
    },
    en: {
      title: `Frequently Asked Questions (FAQ) | ${SITE_NAME}`,
      description:
        'Answers to frequently asked questions about astrological reports, consultations, payment and delivery.',
      keywords: 'FAQ, frequently asked questions, astrology questions, help',
    },
    zh: {
      title: `常见问题 (FAQ) | ${SITE_NAME}`,
      description: '关于占星报告、咨询、支付和交付的常见问题解答。',
      keywords: 'FAQ, 常见问题, 占星问题, 帮助',
    },
    es: {
      title: `Preguntas Frecuentes (FAQ) | ${SITE_NAME}`,
      description:
        'Respuestas a preguntas frecuentes sobre informes astrológicos, consultas, pago y entrega.',
      keywords: 'FAQ, preguntas frecuentes, preguntas astrología, ayuda',
    },
  },

  'for-astrologers': {
    ru: {
      title: `Для астрологов — Присоединяйтесь к платформе | ${SITE_NAME}`,
      description:
        'Станьте частью Astriqo. Проводите консультации, получайте клиентов, управляйте расписанием. Для профессиональных астрологов.',
      keywords:
        'для астрологов, работа астрологом, платформа для астрологов, астролог вакансия',
    },
    en: {
      title: `For Astrologers — Join the Platform | ${SITE_NAME}`,
      description:
        'Become part of Astriqo. Conduct consultations, get clients, manage your schedule. For professional astrologers.',
      keywords:
        'for astrologers, astrologer work, astrologer platform, astrologer job',
    },
    zh: {
      title: `占星师入驻 — 加入平台 | ${SITE_NAME}`,
      description:
        '成为Astriqo的一员。进行咨询、获取客户、管理日程。面向专业占星师。',
      keywords: '占星师入驻, 占星师工作, 占星师平台, 占星师职位',
    },
    es: {
      title: `Para Astrólogos — Únete a la Plataforma | ${SITE_NAME}`,
      description:
        'Sé parte de Astriqo. Realiza consultas, consigue clientes, gestiona tu agenda. Para astrólogos profesionales.',
      keywords:
        'para astrólogos, trabajo astrólogo, plataforma astrólogos, empleo astrólogo',
    },
  },

  about: {
    ru: {
      title: `О нас — Команда и миссия | ${SITE_NAME}`,
      description:
        'Узнайте о команде Astriqo, нашей миссии и подходе к астрологии. Мы делаем астрологию доступной и профессиональной.',
      keywords: 'о нас, команда Astriqo, миссия, астрология платформа',
    },
    en: {
      title: `About Us — Team & Mission | ${SITE_NAME}`,
      description:
        'Learn about the Astriqo team, our mission and approach to astrology. We make astrology accessible and professional.',
      keywords: 'about us, Astriqo team, mission, astrology platform',
    },
    zh: {
      title: `关于我们 — 团队与使命 | ${SITE_NAME}`,
      description:
        '了解Astriqo团队、我们的使命和占星方法。我们让占星术变得专业且易于获取。',
      keywords: '关于我们, Astriqo团队, 使命, 占星平台',
    },
    es: {
      title: `Sobre Nosotros — Equipo y Misión | ${SITE_NAME}`,
      description:
        'Conoce al equipo de Astriqo, nuestra misión y enfoque de la astrología. Hacemos la astrología accesible y profesional.',
      keywords: 'sobre nosotros, equipo Astriqo, misión, plataforma astrología',
    },
  },

  contact: {
    ru: {
      title: `Контакты | ${SITE_NAME}`,
      description:
        'Свяжитесь с нами: email, телефон, форма обратной связи. Мы ответим в течение 24 часов.',
      keywords: 'контакты, связаться, обратная связь, поддержка',
    },
    en: {
      title: `Contact Us | ${SITE_NAME}`,
      description:
        'Get in touch: email, phone, contact form. We respond within 24 hours.',
      keywords: 'contact, get in touch, feedback, support',
    },
    zh: {
      title: `联系我们 | ${SITE_NAME}`,
      description: '联系我们：邮箱、电话、联系表单。我们将在24小时内回复。',
      keywords: '联系, 联系我们, 反馈, 支持',
    },
    es: {
      title: `Contacto | ${SITE_NAME}`,
      description:
        'Contáctanos: email, teléfono, formulario de contacto. Respondemos en 24 horas.',
      keywords: 'contacto, contactar, comentarios, soporte',
    },
  },

  marketplace: {
    ru: {
      title: `Маркетплейс услуг — Все астрологические услуги | ${SITE_NAME}`,
      description:
        'Все астрологические услуги в одном месте: отчёты, консультации, пакеты. Сравните и выберите подходящий вариант.',
      keywords:
        'маркетплейс астрология, астрологические услуги, каталог услуг, астрология онлайн',
    },
    en: {
      title: `Services Marketplace — All Astrological Services | ${SITE_NAME}`,
      description:
        'All astrological services in one place: reports, consultations, packages. Compare and choose the right option.',
      keywords:
        'astrology marketplace, astrological services, services catalog, astrology online',
    },
    zh: {
      title: `服务市场 — 所有占星服务 | ${SITE_NAME}`,
      description:
        '所有占星服务集于一处：报告、咨询、套餐。比较并选择合适的选项。',
      keywords: '占星市场, 占星服务, 服务目录, 在线占星',
    },
    es: {
      title: `Marketplace de Servicios — Todos los Servicios Astrológicos | ${SITE_NAME}`,
      description:
        'Todos los servicios astrológicos en un solo lugar: informes, consultas, paquetes. Compara y elige la opción adecuada.',
      keywords:
        'marketplace astrología, servicios astrológicos, catálogo servicios, astrología online',
    },
  },

  privacy: {
    ru: {
      title: `Политика конфиденциальности | ${SITE_NAME}`,
      description:
        'Политика конфиденциальности Astriqo. Как мы собираем, используем и защищаем ваши персональные данные.',
      keywords:
        'политика конфиденциальности, персональные данные, защита данных',
    },
    en: {
      title: `Privacy Policy | ${SITE_NAME}`,
      description:
        'Astriqo privacy policy. How we collect, use and protect your personal data.',
      keywords: 'privacy policy, personal data, data protection',
    },
    zh: {
      title: `隐私政策 | ${SITE_NAME}`,
      description: 'Astriqo隐私政策。我们如何收集、使用和保护您的个人数据。',
      keywords: '隐私政策, 个人数据, 数据保护',
    },
    es: {
      title: `Política de Privacidad | ${SITE_NAME}`,
      description:
        'Política de privacidad de Astriqo. Cómo recopilamos, usamos y protegemos tus datos personales.',
      keywords: 'política de privacidad, datos personales, protección de datos',
    },
  },

  terms: {
    ru: {
      title: `Условия использования | ${SITE_NAME}`,
      description:
        'Условия использования платформы Astriqo. Правила пользования сервисом, оплата, возвраты.',
      keywords: 'условия использования, правила, пользовательское соглашение',
    },
    en: {
      title: `Terms of Service | ${SITE_NAME}`,
      description:
        'Astriqo terms of service. Platform usage rules, payment, refunds.',
      keywords: 'terms of service, rules, user agreement',
    },
    zh: {
      title: `使用条款 | ${SITE_NAME}`,
      description: 'Astriqo使用条款。平台使用规则、支付、退款。',
      keywords: '使用条款, 规则, 用户协议',
    },
    es: {
      title: `Términos de Uso | ${SITE_NAME}`,
      description:
        'Términos de uso de Astriqo. Reglas de uso de la plataforma, pago, reembolsos.',
      keywords: 'términos de uso, reglas, acuerdo de usuario',
    },
  },
}

// Fallback for pages not in the config
const defaultSeo: Record<Language, PageSeo> = {
  ru: {
    title: `${SITE_NAME} — Профессиональная астрология`,
    description:
      'Платформа для глубокого самопознания через профессиональную астрологию.',
    keywords: 'астрология, натальная карта, гороскоп',
  },
  en: {
    title: `${SITE_NAME} — Professional Astrology`,
    description:
      'A platform for deep self-discovery through professional astrology.',
    keywords: 'astrology, birth chart, horoscope',
  },
  zh: {
    title: `${SITE_NAME} — 专业占星术`,
    description: '通过专业占星术深度自我认知的平台。',
    keywords: '占星术, 本命盘, 星座运势',
  },
  es: {
    title: `${SITE_NAME} — Astrología Profesional`,
    description:
      'Plataforma de autoconocimiento profundo a través de la astrología profesional.',
    keywords: 'astrología, carta natal, horóscopo',
  },
}

export function getPageSeo(page: string, language: Language): PageSeo {
  return seoConfig[page]?.[language] || defaultSeo[language]
}

export function getCanonicalUrl(page: string): string {
  const base = 'https://astriqo.com'
  if (page === 'home') return base
  return `${base}/#/${page}`
}

export function getOgLocale(language: Language): string {
  const localeMap: Record<Language, string> = {
    ru: 'ru_RU',
    en: 'en_US',
    zh: 'zh_CN',
    es: 'es_ES',
  }
  return localeMap[language]
}
